import { DetailsType, FormData } from 'types/application';
import { AA_FIELD_MAPPINGS, setDeep, getDeep } from './aa.map';
import { formatDateTime, formatCancellationDate } from 'utils/application.helpers';

import { 
    OTHER_SECT_TYPE as SECT_TYPE,
    OTHER_SECT_FILER as SECT_FILER,
    OTHER_SECT_APPLICANT as SECT_APPLICANT,
    OTHER_SECT_ADDRESS as SECT_ADDRESS,
    OTHER_SECT_COMPANY as SECT_COMPANY
} from 'common/application.const';
import { payloadDeleteApplication, formDeleteApplication, formWithdrawApplication, payloadWithdrawApplication } from '../helpers';
import commonPayload from './common';

import { decryptId } from 'common/decrypt';

// Build nationality lookup dynamically from cached raw list (preferred) or dropdown options.
const buildNationalityMap = (rows?: { Code: string; Description: string }[] | any[]): Record<string, string> => {
    if (!Array.isArray(rows)) return {};
    const rowsArray = rows as any[]; // <-- cast to avoid union conflict
    return rowsArray.reduce<Record<string, string>>((acc, row) => {
        const code = (row.Code || row.code || '').toString().trim().toUpperCase();
        const desc = (row.Description || row.description || '').toString().trim();
        if (code) acc[code] = desc || code;
        return acc;
    }, {});
};


type Props = {
    myInfoContent: any;
    uen?: string;
    id?: string;
};

const autoFillData = async ({ myInfoContent, uen, id }: Props) => {
    const decryptedID = await decryptId(id || '', 'decrypt');
    const decryptedUEN = await decryptId(uen || '', 'decrypt');
    const person = myInfoContent?.corppass || myInfoContent || {};
    const entity = myInfoContent?.['basic-profile'] || {};
    const corpUser = person?.['corppass-user'] || {};
    const address = myInfoContent?.address || person?.regadd || {};

    const isFin = ['F', 'G', 'M'].some((pre: string) => person?.uinfin?.value?.startsWith(pre));

    let idType = '';
    if (isFin) idType = 'FIN';
    if (person?.nationality?.code && !isFin) {
        idType = person.nationality.code === 'SG' ? 'NRC' : 'NRP';
    }

    let salutation = '';
    if (person.sex) {
        salutation = person.sex?.code === 'M' ? 'Mr.' : 'Ms.';
    }

    let corpUserID = corpUser['id-type']?.desc;
    if (corpUserID === 'NRIC') corpUserID = 'NRC';

    const generalForm = {
        id: 'general',
        formID: 'general',
        title: 'Applicant/Filer Details',
        hasError: true,
        values: {
            // Ensure applicant-type present
            [`${SECT_TYPE}-applicant-type`]: 'Applicant',
            [`${SECT_FILER}-id-type`]: corpUserID || idType,
            [`${SECT_FILER}-idno`]: decryptedID,
            [`${SECT_FILER}-salutaion`]: salutation,
            [`${SECT_FILER}-name`]: corpUser.name?.value || person?.name?.value,
            [`${SECT_FILER}-mobile-no-country-code`]: person?.mobileno?.areacode?.value || '65',
            [`${SECT_FILER}-mobile-no-main`]: person?.mobile?.value || person?.mobileno?.nbr?.value,
            [`${SECT_FILER}-office-no-country-code`]: '65',
            [`${SECT_FILER}-office-no-main`]: '',
            [`${SECT_FILER}-email`]: person?.email?.value,
            [`${SECT_APPLICANT}-id-type`]: corpUserID || idType,
            [`${SECT_APPLICANT}-idno`]: decryptedID,
            [`${SECT_APPLICANT}-salutaion`]: salutation,
            [`${SECT_APPLICANT}-name`]: corpUser.name?.value || person?.name?.value,
            [`${SECT_APPLICANT}-nationality`]: person?.nationality?.code,
            [`${SECT_APPLICANT}-mobile-no-country-code`]: person?.mobileno?.areacode?.value || '65',
            [`${SECT_APPLICANT}-mobile-no-main`]: person?.mobile?.value || person?.mobileno?.nbr?.value,
            [`${SECT_APPLICANT}-email`]: person?.email?.value,
            // Company fields for Corppass
            [`${SECT_COMPANY}-uen-no`]: decryptedUEN,
            [`${SECT_COMPANY}-entity-type`]: entity['entity-type']?.code || entity['entity-type']?.value || '',
            [`${SECT_COMPANY}-company`]: entity['entity-name']?.value,
            // For AA, foreign company name should be empty for new applications (users can enter any foreign company)
            // Foreign company name will be populated from backend when editing an existing application
            [`${SECT_COMPANY}-foreign-company-name`]: '',
            [`${SECT_COMPANY}-office-no-country-code`]: '65',
            [`${SECT_COMPANY}-office-no-main`]: '',
            [`${SECT_COMPANY}-address-type`]: address.type || address.addresstype || 'SG',
            [`${SECT_COMPANY}-postal`]: address.postal?.value,
            [`${SECT_COMPANY}-block-house`]: address.block?.value,
            [`${SECT_COMPANY}-street`]: address.street?.value,
            [`${SECT_COMPANY}-floor`]: address.floor?.value,
            [`${SECT_COMPANY}-unit`]: address.unit?.value,
            [`${SECT_COMPANY}-building`]: address.building?.value,
            // Address fields for Singpass  
            [`${SECT_ADDRESS}-address-type`]: address.type || address.addresstype || 'SG',
            [`${SECT_ADDRESS}-postal`]: address.postal?.value,
            [`${SECT_ADDRESS}-block-house`]: address.block?.value,
            [`${SECT_ADDRESS}-street`]: address.street?.value,
            [`${SECT_ADDRESS}-floor`]: address.floor?.value,
            [`${SECT_ADDRESS}-unit`]: address.unit?.value,
            [`${SECT_ADDRESS}-building`]: address.building?.value
        }
    };

    return generalForm;
};


const formatRetrieve = async (
    content: any,
    action?: string,
    nationalityData?: { Code: string; Description: string }[],
    dropdownOpts?: any,
    application?: string,
    caseTypeID?: string
) => {
    if (action === 'application-delete') return formDeleteApplication;
    if (action === 'application-withdraw') return formWithdrawApplication;
    
    // Handle download action - return application download files
    if (action === 'download') {
        return await commonPayload.getApplicationDownload(caseTypeID, content?.licenceName || 'Registration of Accounting Authority');
    }
    
    // New contract: ignore legacy root Filer/Applicant nodes; rely on LICApplication subtree.
    const licApp = content.LICApplication || {};
    const licApplicant = licApp.applicant || {};
    const licFiler = licApp.filer || {};
    const licCompany = licApp.company || {};
    const licCompanyAddr = licCompany.registeredAddress || {};
    const profile = licApp.profile || {};
    const appliedAs = profile.applyAs === 'On behalf of applicant' ? 'Filer' : 'Applicant';

    // Addresses (flattened) already stored directly in applicant/filer.address
    const applicantAddress = licApplicant.address || {};
    // (We currently do not store a separate filer address from form; left blank earlier.)

    const nationalityMap = buildNationalityMap(
        nationalityData || dropdownOpts?.Nationality?.map((o: any) => ({ Code: o.optID, Description: o.optLabel }))
    );

    // Check if this is cancel mode
    const isCancelMode = application === 'IMDA-IRIS-Work-LICApplication-AA-CANCEL';
    const cancelSuffix = isCancelMode ? '-cancel' : '';

    const generalForm = {
        id: `general${cancelSuffix}`,
        formID: `general${cancelSuffix}`,
        title: 'Applicant/Filer Details',
        hasError: true,
        values: {
            [`${SECT_TYPE}-applicant-type`]: appliedAs,
            // Application number for cancel mode (read from pyID or applicationNumber)
            ...(isCancelMode && {
                [`general${cancelSuffix}-application-number`]: content?.applicationNumber || content?.pyID || ''
            }),
            // Filer (only if not Applicant)
            [`${SECT_FILER}-id-type`]: appliedAs === 'Filer' ? licFiler.idType : licApplicant.idType,
            [`${SECT_FILER}-idno`]: appliedAs === 'Filer' ? licFiler.idNumber : licApplicant.idNumber,
            [`${SECT_FILER}-salutaion`]: appliedAs === 'Filer' ? licFiler.salutation : licApplicant.salutation,
            [`${SECT_FILER}-name`]: appliedAs === 'Filer' ? licFiler.name : licApplicant.name,
            // Phone (ensure country code + main split; backend may only return unified contactNumber)
            [`${SECT_FILER}-mobile-no-country-code`]: '65',
            [`${SECT_FILER}-mobile-no-main`]: appliedAs === 'Filer' ? (licFiler.contactNumber || licFiler.mobileNumber || '') : (licApplicant.contactNumber || licApplicant.mobileNumber || ''),
            [`${SECT_FILER}-office-no-country-code`]: '65',
            [`${SECT_FILER}-office-no-main`]: appliedAs === 'Filer' ? (licFiler.officeNumber || licFiler.officeTelNumber || '') : (licApplicant.officeNumber || licApplicant.officeTelNumber || ''),
            [`${SECT_FILER}-email`]: appliedAs === 'Filer' ? licFiler.email : licApplicant.email,
            // Applicant
            [`${SECT_APPLICANT}-id-type`]: licApplicant.idType,
            [`${SECT_APPLICANT}-idno`]: licApplicant.idNumber,
            [`${SECT_APPLICANT}-salutaion`]: licApplicant.salutation,
            [`${SECT_APPLICANT}-name`]: licApplicant.name,
            [`${SECT_APPLICANT}-nationality`]: licApplicant.nationality,
            // Pre-resolved label to avoid dependency on dropdown timing in review renderer
            [`${SECT_APPLICANT}-nationality-label`]: nationalityMap[(licApplicant.nationality || '').toUpperCase()] || licApplicant.nationality || '',
            [`${SECT_APPLICANT}-uen-no`]: licApplicant.companyUEN || licCompany.uen,
            [`${SECT_APPLICANT}-company`]: licApplicant.companyName || licCompany.companyName,
            [`${SECT_APPLICANT}-mobile-no-country-code`]: '65',
            [`${SECT_APPLICANT}-mobile-no-main`]: licApplicant.contactNumber || licApplicant.mobileNumber || '',
            [`${SECT_APPLICANT}-email`]: licApplicant.email,
            // Company details for Corppass
            [`${SECT_COMPANY}-uen-no`]: licApplicant.companyUEN || licCompany.uen,
            [`${SECT_COMPANY}-entity-type`]: licCompany.entityType || '',
            [`${SECT_COMPANY}-company`]: licCompany.CareOf || licApplicant.companyName || licCompany.companyName,
            [`${SECT_COMPANY}-iAmRepresentingAForeignCompany`]: licCompany.CareOf ? 'Yes' : 'No',
            [`${SECT_COMPANY}-foreign-company-name`]: licCompany.companyName || '',
            [`${SECT_COMPANY}-office-no-country-code`]: '65',
            [`${SECT_COMPANY}-office-no-main`]: licApplicant.officeNumber || licApplicant.officeTelNumber || '',
            [`${SECT_COMPANY}-postal`]: licCompanyAddr.postalCode || licCompanyAddr.postalcode || '',
            [`${SECT_COMPANY}-block-house`]: licCompanyAddr.blockNo || licCompanyAddr.blockno || '',
            [`${SECT_COMPANY}-street`]: licCompanyAddr.streetName || licCompanyAddr.streetname || '',
            [`${SECT_COMPANY}-floor`]: licCompanyAddr.floor || '',
            [`${SECT_COMPANY}-unit`]: licCompanyAddr.unit || '',
            [`${SECT_COMPANY}-building`]: licCompanyAddr.buildingName || licCompanyAddr.buildingname || '',
            // Address for Singpass
            [`${SECT_ADDRESS}-postal`]: applicantAddress.postalCode || licCompanyAddr.postalcode,
            [`${SECT_ADDRESS}-block-house`]: applicantAddress.blockNo || licCompanyAddr.blockno,
            [`${SECT_ADDRESS}-street`]: applicantAddress.streetName || licCompanyAddr.streetname,
            [`${SECT_ADDRESS}-floor`]: applicantAddress.floor || licCompanyAddr.floor,
            [`${SECT_ADDRESS}-unit`]: applicantAddress.unit || licCompanyAddr.unit,
            [`${SECT_ADDRESS}-building`]: applicantAddress.buildingName || licCompanyAddr.buildingname
        }
    };

    // Handle cancel mode early - return only general and cancellation forms
    if (isCancelMode) {
        const licenceNode = licApp.licence || {};
        const cancellationInfo = licenceNode?.cancellationInformation || {};
        
        const cancellationForm: FormData = {
            id: 'cancellation-details',
            formID: 'cancellation-details',
            title: 'Cancellation Details',
            hasError: true,
            values: {
                'cancellation-details-application-number': content?.applicationNumber || content?.pyID || '',
                'cancellation-details-uen-no': licApplicant.companyUEN || licCompany.uen || '',
                'cancellation-details-licence-no': content?.licenceNumber || licenceNode.licenceNumber || '',
                'cancellation-details-effective-date': cancellationInfo.effectiveDateOfCancellation || '',
                'cancellation-details-cancellation-reason': cancellationInfo.cancellationReason || '',
                'cancellation-details-others-reason': cancellationInfo.ifOthersPleaseStateReason || '',
                'cancellation-details-remarks': cancellationInfo.Remarks || ''
            }
        };
        
        return [generalForm, cancellationForm];
    }

    // Add placeholder forms so wizard shows all AA sections defined in INDUSTRIES (contact & application details)
    // Contact & Billing details population (previously placeholder). Map from nested licence nodes if present.
    const contactPerson = licApp.licence?.contactPersonParticulars || {};
    const contactAddress = licApp.licence?.contactPersonAddress || {};
    const billingPerson = licApp.licence?.billingPersonParticulars || {};
    const billingAddress = licApp.licence?.billingAddress || {};
    const contactAddr = contactAddress.address || {};
    const billingAddr = billingAddress.address || {};
    // Helper to normalise backend same-as flags: only return 'Yes' if explicitly 'Yes' or true; else '' so UI shows unselected.
    // Reverted: retain earlier behavior. If backend sends 'Yes'/true -> 'Yes', if 'No'/false -> 'No', else empty string.
    const yesIf = (v: any) => (v === 'Yes' || v === true ? 'Yes' : v === 'No' || v === false ? 'No' : '');
    const sameAsApplicantVal = yesIf(contactPerson.sameAsApplicantParticulars);
    const sameAsCompanyAddrVal = yesIf(contactAddress.sameAsCompanyRegisteredAddress);
    const sameAsContactParticularsVal = yesIf(billingPerson.sameAsContactPerson || billingPerson.sameAsContactPersonParticulars);
    const sameAsContactAddressVal = yesIf(billingAddress.sameAsContactPersonAddress || billingAddress.sameAsMailingAddress);
    const contactBillingForm: FormData = {
        id: 'contact-billing-details',
        formID: 'contact-billing-details',
        title: 'Billing Details',
        hasError: true,
        values: {
            // Section: contact-particulars (sectionID = 'contact-particulars')
            'contact-particulars-same-as-applicant': sameAsApplicantVal,
            'contact-particulars-contactName': contactPerson.name,
            'contact-particulars-designation': contactPerson.designation,
            'contact-particulars-telNumber': contactPerson.officeTelNumber,
            'contact-particulars-mobileNo': contactPerson.mobileNumber,
            'contact-particulars-email': contactPerson.email,
            'contact-particulars-faxNo': contactPerson.faxNumber,
            // Canonical keys expected: `${sectionID}-${fieldId}` e.g. 'contact-particulars-same-as-applicant'
            // Provide raw fallback keys (without section prefix) to support renderer fallback logic
            'same-as-applicant': sameAsApplicantVal,
            'contactName': contactPerson.name,
            'designation': contactPerson.designation,
            'telNumber': contactPerson.officeTelNumber,
            'mobileNo': contactPerson.mobileNumber,
            'email': contactPerson.email,
            'faxNo': contactPerson.faxNumber,
            // Section: contact-address
            'contact-address-same-as-company-address': sameAsCompanyAddrVal,
            'contact-address-postal': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.postalCode || '' : '',
            'contact-address-blockHouse': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.blockNo || '' : '',
            'contact-address-street': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.streetName || '' : '',
            'contact-address-floor': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.floor || '' : '',
            'contact-address-unit': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.unit || '' : '',
            'contact-address-building': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.buildingName || '' : '',
            'same-as-company-address': sameAsCompanyAddrVal,
            // Section: billing-particulars
            'billing-particulars-same-as-contact': sameAsContactParticularsVal,
            'billing-particulars-billingName': billingPerson.name,
            'billing-particulars-designation': billingPerson.designation,
            'billing-particulars-telNumber': billingPerson.officeTelNumber,
            'billing-particulars-mobileNo': billingPerson.mobileNumber,
            'billing-particulars-email': billingPerson.email,
            'billing-particulars-faxNo': billingPerson.faxNumber,
            'same-as-contact': sameAsContactParticularsVal,
            'billingName': billingPerson.name,
            // reuse designation/telNumber/mobileNo/email raw keys already set above if overlapping naming
            // Section: billing-address
            'billing-address-same-as-contact-address': sameAsContactAddressVal,
            'billing-address-postal': sameAsContactAddressVal !== 'Yes' ? billingAddr.postalCode || '' : '',
            'billing-address-blockHouse': sameAsContactAddressVal !== 'Yes' ? billingAddr.blockNo || '' : '',
            'billing-address-street': sameAsContactAddressVal !== 'Yes' ? billingAddr.streetName || '' : '',
            'billing-address-floor': sameAsContactAddressVal !== 'Yes' ? billingAddr.floor || '' : '',
            'billing-address-unit': sameAsContactAddressVal !== 'Yes' ? billingAddr.unit || '' : '',
            'billing-address-building': sameAsContactAddressVal !== 'Yes' ? billingAddr.buildingName || '' : '',
            'same-as-contact-address': sameAsContactAddressVal,
            // Section: giro-details (stored under billingAddress)
            'giro-details-doYouWantYourRenewalBePaidThroughGiro': yesIf(billingAddress.doYouWantYourRenewalBePaidThroughGiro),
            'giro-details-doYouHaveAnExistingGiroArrangementWithImda': yesIf(billingAddress.doYouHaveAnExistingGiroArrangementWithImda),
            'giro-details-bank': billingAddress.bank || '',
            'giro-details-bankAccountNumber': billingAddress.bankAccountNumber || ''
        }
    };
    // --- AA Application Details (ship registration focused) ---
    // Read from licence.additionalInfo according to correct backend contract
    const licenceNode = getDeep(content, 'LICApplication.licence') || {};
    const additionalInfoNode = licenceNode.additionalInfo || {};
    
    // Normalise ship registration data from additionalInfo.registeredShipList
    const shipDataRaw: any[] = additionalInfoNode.registeredShipList || [];
    const mappedShipData = (Array.isArray(shipDataRaw) ? shipDataRaw : [])
        .map((ship: any) => {
            return {
                shipName: ship.shipName || '',
                callSign: ship.callSign || '',
                licenceNo: ship.licenceNo || '',
                aaicCode: ship.aaicCode || '',
                mmsiNo: ship.mmsiNo || ''
            };
        })
        // Drop rows that are completely empty
        .filter(ship => Object.values(ship).some(v => v !== '' && v !== undefined && v !== null));

    const applicationDetailsForm: FormData = {
        id: 'application-details',
        formID: 'application-details',
        title: 'Application Details',
        hasError: true,  // Will be recalculated by system after values are set
        values: {
            // Expose backend case identifier for display
            'application-details-caseid': content.caseid || content.caseID || licApp.caseid || licApp.caseID || '',
            // Application number (preferred display identifier)
            'application-details-application-number': content.applicationNumber || licApp.applicationNumber || '',
            // Licence name (fallback to static if backend omits)
            'application-details-licence-name': licApp.licence?.licenceName || 'Registration of Accounting Authority',
            // AA-specific fields from additionalInfo
            'capital-details-company-paid-up-capital': additionalInfoNode.companyPaidUpCapital || '',
            'registered-ship-information-submission-method': additionalInfoNode.submissionMethod || '',
            'registered-ship-information-aaic-code': additionalInfoNode.aaicCode || '',
            'registered-ship-information-scheme-of-service': additionalInfoNode.serviceTermsAndFees || '',
            // Ship registration data
            'registered-ship-information-list-registered-ships': mappedShipData
        }
    };

    // Check if this is view mode (when we should show summary)
    // Similar to event payload pattern - if no specific action and content has case data, show summary
    const isViewMode = application === 'IMDA-IRIS-Work-LICApplication-AA-VIEW' || 
                      (content?.caseID || content?.caseid || content?.id) && !action;
    
    if (isViewMode) {
        const summaryForm = {
            id: 'summary',
            formID: 'summary',
            title: 'Application Summary',
            hasError: false,
            values: {
                // Using summary- prefix like all other payloads for proper field mapping
                'summary-transactionType': content?.transactionType || content?.Type || 'New',
                'summary-application-name': content?.licenceName || content?.pyLabel || 'Registration of Accounting Authority',
                'summary-application-number': content?.applicationNumber || content?.pyID || '',
                'summary-application-businessStatus': content?.CustomerStatus || content?.BusinessStatus || 'In Progress',
                'summary-application-submission': formatDateTime(content?.pxCreateDateTime || content?.ApplicationDate || '', true)
            }
        };
        
        return [summaryForm, generalForm, contactBillingForm, applicationDetailsForm];
    }

    // For creation mode (AA), no summary form
    return [generalForm, contactBillingForm, applicationDetailsForm];
};

// Removed generic applyGeneralMapping for AA to avoid reintroducing legacy root nodes; explicit construction used instead.

const formatSave = async (details: DetailsType, _caseTypeID: string, pageInstructions: any[] = [], _userRole?: string, action?: string) => {
    if (action === 'application-delete') return payloadDeleteApplication;
    if (action === 'application-withdraw') {
        const withdrawForm = details.forms.find((form: FormData) => form.id === 'application-withdraw')?.values || {};
        const withdrawalReason = String(withdrawForm['aecWithdraw-reason'] || '');
        return payloadWithdrawApplication(withdrawalReason, 'ALT2');
    }
    
    // Detect if this is a cancel case by checking if cancellation-details form exists
    const isCancelMode = details.forms.some((form: FormData) => form.id === 'cancellation-details');
    
    // Handle cancel mode - create cancellation payload
    if (isCancelMode) {
        const general = details.forms.find((form: FormData) => form.id === 'general-cancel')?.values || {};
        const cancellationDetails = details.forms.find((form: FormData) => form.id === 'cancellation-details')?.values || {};
        const appliedAsValue = general[`${SECT_TYPE}-applicant-type`];
        
        const cancelContent: any = {
            licenceName: 'Registration of Accounting Authority',
            transactionType: 'Cancel',
            AppSource: 'FEP',
            Type: 'Individual',
            pyWorkIDPrefix: 'LI-',
            LICApplication: {
                pxObjClass: 'IMDA-IRIS-Data-Licence-Application',
                applicant: {
                    pxObjClass: 'IMDA-IRIS-Data-Licence-Applicant',
                    idType: general[`${SECT_APPLICANT}-id-type`],
                    companyUEN: general[`${SECT_APPLICANT}-uen-no`] || general[`${SECT_COMPANY}-uen-no`],
                    companyName: general[`${SECT_APPLICANT}-company`] || general[`${SECT_COMPANY}-company`],
                    nationality: general[`${SECT_APPLICANT}-nationality`],
                    contactNumber: general[`${SECT_APPLICANT}-mobile-no-main`],
                    name: general[`${SECT_APPLICANT}-name`],
                    salutation: general[`${SECT_APPLICANT}-salutaion`],
                    idNumber: general[`${SECT_APPLICANT}-idno`],
                    email: general[`${SECT_APPLICANT}-email`]
                },
                filer: appliedAsValue !== 'Applicant' ? {
                    pxObjClass: 'IMDA-IRIS-Data-Licence-Applicant',
                    idType: general[`${SECT_FILER}-id-type`],
                    idNumber: general[`${SECT_FILER}-idno`],
                    email: general[`${SECT_FILER}-email`],
                    name: general[`${SECT_FILER}-name`],
                    salutation: general[`${SECT_FILER}-salutaion`],
                    contactNumber: general[`${SECT_FILER}-mobile-no-main`]
                } : undefined,
                company: {
                    pxObjClass: 'IMDA-IRIS-Data-Licence-Company',
                    uen: general[`${SECT_COMPANY}-uen-no`] || general[`${SECT_APPLICANT}-uen-no`],
                    companyName: general[`${SECT_COMPANY}-company`] || general[`${SECT_APPLICANT}-company`],
                    entityType: general[`${SECT_COMPANY}-entity-type`],
                    CareOf: general[`${SECT_COMPANY}-iAmRepresentingAForeignCompany`] === 'Yes' ? general[`${SECT_COMPANY}-company`] : undefined,
                    registeredAddress: {
                        pxObjClass: 'IMDA-IRIS-Data-Licence-Address',
                        buildingName: general[`${SECT_COMPANY}-building`],
                        streetName: general[`${SECT_COMPANY}-street`],
                        unit: general[`${SECT_COMPANY}-unit`],
                        blockno: general[`${SECT_COMPANY}-block-house`],
                        postalcode: general[`${SECT_COMPANY}-postal`],
                        floor: general[`${SECT_COMPANY}-floor`]
                    }
                },
                licence: {
                    cancellationInformation: {
                        pxObjClass: 'IMDA-IRIS-Data-Cancellation',
                        effectiveDateOfCancellation: formatCancellationDate(cancellationDetails['cancellation-details-effective-date']),
                        cancellationReason: cancellationDetails['cancellation-details-cancellation-reason'] || '',
                        ifOthersPleaseStateReason: cancellationDetails['cancellation-details-others-reason'] || '',
                        Remarks: cancellationDetails['cancellation-details-remarks'] || ''
                    }
                },
                profile: {
                    pxObjClass: 'IMDA-IRIS-Data-Licence-Application',
                    applyAs: appliedAsValue === 'Applicant' ? 'Applicant' : 'On behalf of applicant'
                }
            }
        };
        
        // Remove filer if not needed
        if (appliedAsValue === 'Applicant') {
            delete cancelContent.LICApplication.filer;
        }
        
        return {
            caseTypeID: 'IMDA-IRIS-Work-LICApplication',
            content: cancelContent
        };
    }
    
    const general = details.forms.find((form: FormData) => form.id === 'general')?.values || {};
    const contactBilling = details.forms.find((form: FormData) => form.id === 'contact-billing-details')?.values || {};
    const appliedAsValue = general[`${SECT_TYPE}-applicant-type`];
    // (Removed unused generalIDno / generalBDay in revised contract)

    // AA specific: no EventList processing, so no per-event page instructions.
    let pageInstructionsForDelete: any[] = [];

    const baseContent: any = {
        licenceName: 'Registration of Accounting Authority',
        transactionType: 'New',
        AppSource: 'FEP',
        Type: 'Individual',
        LICApplication: {
            applicant: {},
            filer: {},
            company: { registeredAddress: {} },
            licence: {},
            profile: { applyAs: appliedAsValue === 'Applicant' ? 'Applicant' : 'On behalf of applicant' }
        }
    };

    // Apply only contact & billing mapping into nested licence structure (these mappings target LICApplication.* paths)
    AA_FIELD_MAPPINGS.filter(m => m.formId === 'contact-billing-details').forEach(m => {
        const value = contactBilling[m.fieldId];
        // Skip undefined/null, but allow empty string and 'No' for same-as radio fields
        if (value === undefined || value === null) return;
        // For same-as fields, always save the value (Yes/No) even if empty
        const isSameAsField = m.fieldId.includes('same-as');
        if (!isSameAsField && value === '') return;
        setDeep(baseContent, m.backendPath, value);
    });

    //

    // AA Application details form values (prefer application-details, but fall back to any form containing the keys)
    const applicationDetails = details.forms.find((form: FormData) => form.id === 'application-details')?.values || {};
    const findInAllForms = (candidates: string[]): any => {
        for (const form of details.forms as FormData[]) {
            for (const key of candidates) {
                const v = (form.values as any)?.[key];
                if (v !== undefined && v !== null && v !== '') return v;
            }
        }
        return undefined;
    };
    
    // AA-specific fields
    const paidUpCapital = applicationDetails['capital-details-company-paid-up-capital'] || 
                         applicationDetails['company-paid-up-capital'] || 
                         findInAllForms(['capital-details-company-paid-up-capital', 'company-paid-up-capital']);
    
    const submissionMethod = applicationDetails['registered-ship-information-submission-method'] || 
                            applicationDetails['submission-method'] || 
                            findInAllForms(['registered-ship-information-submission-method', 'submission-method']);
    
    const aaicCode = applicationDetails['registered-ship-information-aaic-code'] || 
                    applicationDetails['aaic-code'] || 
                    findInAllForms(['registered-ship-information-aaic-code', 'aaic-code']);
    
    const schemeOfService = applicationDetails['registered-ship-information-scheme-of-service'] || 
                           applicationDetails['scheme-of-service'] || 
                           findInAllForms(['registered-ship-information-scheme-of-service', 'scheme-of-service']);

    // Ensure licence + AAApplicationDetails node exist
    baseContent.LICApplication = baseContent.LICApplication || {};
    baseContent.LICApplication.licence = baseContent.LICApplication.licence || {};
    const licence = baseContent.LICApplication.licence;

    // Build AAApplicationDetails object following backend requirements
    const aaApplicationDetails: any = {};
    if (paidUpCapital) aaApplicationDetails.PaidUpCapital = paidUpCapital;
    if (submissionMethod) aaApplicationDetails.SubmissionMethod = submissionMethod;
    if (aaicCode) aaApplicationDetails.AAICCode = aaicCode;
    if (schemeOfService) aaApplicationDetails.SchemeOfService = schemeOfService;
    
    // Ship Registration Data mapping (manual / bulk). Expect array of ship objects from UI under field id 'list-registered-ships'.
    const uiShipDataRaw = (applicationDetails['registered-ship-information-list-registered-ships']) ?? 
                         applicationDetails['list-registered-ships'] ?? 
                         findInAllForms(['registered-ship-information-list-registered-ships', 'list-registered-ships']);
    const uiShipData: any[] = Array.isArray(uiShipDataRaw) ? uiShipDataRaw : [];
    const registeredShipList = uiShipData
        .map(ship => {
            if (!ship || typeof ship !== 'object') return null;
            return {
                shipName: (ship.shipName || '').trim(),
                callSign: (ship.callSign || '').trim(),
                licenceNo: (ship.licenceNo || '').trim(),
                aaicCode: (ship.aaicCode || '').trim(),
                mmsiNo: (ship.mmsiNo || '').trim()
            };
        })
        // Keep only rows with at least one core value (ship name or call sign or licence)
        .filter(Boolean)
        .filter((ship: any) => [ship.shipName, ship.callSign, ship.licenceNo].some(v => v));

    // Build additionalInfo structure according to the correct backend contract
    const additionalInfo: any = {};
    if (paidUpCapital) additionalInfo.companyPaidUpCapital = paidUpCapital;
    if (schemeOfService) additionalInfo.serviceTermsAndFees = schemeOfService;
    if (submissionMethod) additionalInfo.submissionMethod = submissionMethod;
    if (aaicCode) additionalInfo.aaicCode = aaicCode;
    
    // Only include registeredShipList if it has data (don't send empty array)
    if (registeredShipList.length > 0) {
        additionalInfo.registeredShipList = registeredShipList;
    }

    // Set additionalInfo in licence if there's any data to send
    if (Object.keys(additionalInfo).length > 0) {
        licence.additionalInfo = additionalInfo;
    }

    // Build applicant & filer nodes per required contract structure (simple flattened address/contact)
    const applicantNode: any = {
        idType: general[`${SECT_APPLICANT}-id-type`],
        address: {
            buildingName: general[`${SECT_ADDRESS}-building`],
            streetName: general[`${SECT_ADDRESS}-street`],
            unit: general[`${SECT_ADDRESS}-unit`],
            blockNo: general[`${SECT_ADDRESS}-block-house`],
            postalCode: general[`${SECT_ADDRESS}-postal`],
            floor: general[`${SECT_ADDRESS}-floor`]
        },
        contactNumber: general[`${SECT_APPLICANT}-mobile-no-main`],
        name: general[`${SECT_APPLICANT}-name`],
        salutation: general[`${SECT_APPLICANT}-salutaion`],
        idNumber: general[`${SECT_APPLICANT}-idno`],
        email: general[`${SECT_APPLICANT}-email`]
    };
    const filerNode: any = {
        idType: general[`${SECT_FILER}-id-type`],
        idNumber: general[`${SECT_FILER}-idno`],
        email: general[`${SECT_FILER}-email`],
        name: general[`${SECT_FILER}-name`],
        salutation: general[`${SECT_FILER}-salutaion`],
        contactNumber: general[`${SECT_FILER}-mobile-no-main`],
        address: {
            buildingName: '',
            streetName: '',
            unit: '',
            blockNo: '',
            postalCode: '',
            floor: ''
        }
    };

    baseContent.LICApplication.applicant = applicantNode;
    if (appliedAsValue !== 'Applicant') {
        baseContent.LICApplication.filer = filerNode;
    } else {
        delete baseContent.LICApplication.filer;
    }

    // Determine if user is Singpass (no UEN) or Corppass (has UEN)
    // For Singpass: applicant.address will have data from SECT_ADDRESS
    // For Corppass: company.registeredAddress will have data from SECT_COMPANY
    const hasCompanyData = Boolean(general[`${SECT_COMPANY}-uen-no`] || general[`${SECT_COMPANY}-company`]);
    const isSingpass = !hasCompanyData;
    
    // Add IDNo and IDNoRef based on login type
    if (isSingpass) {
        baseContent.IDNo = general[`${SECT_APPLICANT}-idno`];
        baseContent.IDNoRef = general[`${SECT_APPLICANT}-idno`];
    } else {
        baseContent.IDNo = general[`${SECT_COMPANY}-uen-no`];
        baseContent.IDNoRef = general[`${SECT_COMPANY}-uen-no`];
    }
    
    // Company node mapping - only for Corppass users
    if (hasCompanyData) {
        const isForeignCompany = general[`${SECT_COMPANY}-iAmRepresentingAForeignCompany`] === 'Yes';
        baseContent.LICApplication.company = {
            registeredAddress: {
                buildingName: general[`${SECT_COMPANY}-building`],
                streetName: general[`${SECT_COMPANY}-street`],
                unit: general[`${SECT_COMPANY}-unit`],
                blockNo: general[`${SECT_COMPANY}-block-house`],
                postalCode: general[`${SECT_COMPANY}-postal`],
                floor: general[`${SECT_COMPANY}-floor`]
            },
            entityType: general[`${SECT_COMPANY}-entity-type`] || 'LC',
            uen: general[`${SECT_COMPANY}-uen-no`]
        };
        
        // If representing foreign company: CareOf = local company name, companyName = foreign company name
        // If not representing foreign company: companyName = local company name, no CareOf
        if (isForeignCompany) {
            baseContent.LICApplication.company.CareOf = general[`${SECT_COMPANY}-company`];
            baseContent.LICApplication.company.companyName = general[`${SECT_COMPANY}-foreign-company-name`];
        } else {
            baseContent.LICApplication.company.companyName = general[`${SECT_COMPANY}-company`];
        }
    }

    // Pruning logic: remove structurally empty nodes (backend accepts creation without them)
    const licNode = baseContent.LICApplication.licence || {};
    const examDet = licNode.ExamDetails || {};
    const hasExamDetailsData = Object.keys(examDet).length > 0;
    const otherLicenceKeys = Object.keys(licNode).filter(k => k !== 'ExamDetails');
    if (!hasExamDetailsData && otherLicenceKeys.length === 0) {
        delete baseContent.LICApplication.licence;
    }
    const comp = baseContent.LICApplication.company;
    const compAddr = comp?.registeredAddress || {};
    const compFields = [comp?.companyName, comp?.uen, comp?.entityType];
    const compAddrValues = [compAddr.buildingname, compAddr.streetname, compAddr.unit, compAddr.blockno, compAddr.postalcode, compAddr.floor];
    if (compFields.every(v => !v) && compAddrValues.every(v => !v)) {
        delete baseContent.LICApplication.company;
    }
    const filer = baseContent.LICApplication.filer;
    if (filer) {
        const filerFields = [filer.idType, filer.idNumber, filer.name, filer.email, filer.contactNumber];
        if (filerFields.every(v => !v)) delete baseContent.LICApplication.filer;
    }

    // Basic derived sync logic: if same-as flags are set, copy values accordingly (frontend may already hide fields but ensure backend consistency)
    if (contactBilling['contact-particulars-same-as-applicant'] === 'Yes') {
        // Mirror applicant to contact person
        baseContent.LICApplication = baseContent.LICApplication || {};
        baseContent.LICApplication.licence = baseContent.LICApplication.licence || {};
        const licence = baseContent.LICApplication.licence;
        const applicant = baseContent.LICApplication.applicant || {};
        licence.contactPersonParticulars = {
            ...(licence.contactPersonParticulars || {}),
            sameAsApplicantParticulars: 'Yes',
            name: applicant.name,
            email: applicant.email,
            mobileNumber: applicant.contactNumber,
            officeTelNumber: applicant.OfficeNumber, // Use same contact number for office tel
            salutation: applicant.salutation,
            designation: applicant.designation,
            faxNumber: applicant.faxNumber
        };
    }
    if (contactBilling['billing-particulars-same-as-contact'] === 'Yes') {
        baseContent.LICApplication = baseContent.LICApplication || {};
        baseContent.LICApplication.licence = baseContent.LICApplication.licence || {};
        const licence = baseContent.LICApplication.licence;
        const contactP = licence.contactPersonParticulars || {};
        licence.billingPersonParticulars = {
            ...(licence.billingPersonParticulars || {}),
            sameAsContactPersonParticulars: 'Yes',
            name: contactP.name,
            email: contactP.email,
            mobileNumber: contactP.mobileNumber,
            officeTelNumber: contactP.officeTelNumber,
            salutation: contactP.salutation,
            designation: contactP.designation,
            faxNumber: contactP.faxNumber
        };
    }

    // IMPORTANT: Backend expects canonical LICApplication case type. We still detect AA via
    // content.LICApplication.general.licenceName === 'Radio Operator Exam' and/or applicationType flag elsewhere.
    return {
        caseTypeID: 'IMDA-IRIS-Work-LICApplication',
        content: baseContent,
    // pageInstructionsForDelete is expected to be empty for AA; condition retained for future extensibility.
    ...(pageInstructionsForDelete.length > 0 && { pageInstructions: pageInstructionsForDelete }),
        ...(pageInstructions?.length > 0 && { pageInstructions })
    };
};

// AA does not use performer / event / venue sections; keep minimal emptySet if consumer expects property.
const emptySet = {};

const aaPayload = {
    formatRetrieve,
    formatSave,
    emptySet,
    autoFillData
};

export default aaPayload;
